/*
  The price tool.
  ---------------------------------------------------------------------------
  Vanilla port of lib/quote.ts from the Next app. Four steps, a live price panel,
  and a POST to whatever endpoint the merchant configured in theme settings.

  Two things moved client side because Shopify has no server runtime:

  1. The estimate. It was always safe to compute here; the rates are published on
     the pricing page anyway, so there is nothing to hide.
  2. The lead score. This is prioritization, not authorization. Nothing is gated
     on it, so a tampered score costs the merchant a mis-sorted follow-up queue
     and nothing more. The alternative was standing up a server just to multiply
     six numbers.

  Rates and copy come from window.SS_CONFIG (theme settings). Coverage comes from
  window.SS_GEO (generated from the Next app's markets file).
*/
(function () {
  var CFG = window.SS_CONFIG || {};
  var GEO = window.SS_GEO || { zips: {} };
  var RATES = CFG.rates || {};

  /* ------------------------------------------------------------- pricing */

  /* Anchored to a 2 bed, 2 bath house: every published price is written against
     that size, so the zero point has to match or the headline rate is wrong. */
  var BED_DELTA = { '1': -20, '2': 0, '3': 25, '4': 80, '5': 145 };
  var BATH_DELTA = { '1': -25, '2': 0, '3': 40, '4': 85 };
  var FREQ_MULT = { weekly: 0.88, biweekly: 1, monthly: 1.12, onetime: 1 };
  var VISITS_PER_YEAR = { weekly: 52, biweekly: 26, monthly: 12, onetime: 1 };

  function round5(n) {
    return Math.round(n / 5) * 5;
  }

  function money(n) {
    return '$' + Math.round(n).toLocaleString('en-US');
  }

  function estimate(s) {
    var cleaning = 0;
    if (s.service !== 'junk') {
      var type = s.cleanType || 'recurring';
      cleaning =
        (RATES.cleaning[type] || 0) +
        round5((BED_DELTA[s.bedrooms || '2'] || 0) + (BATH_DELTA[s.bathrooms || '2'] || 0));
      if (type === 'recurring') {
        var mult = FREQ_MULT[s.frequency || 'biweekly'];
        /* Only round once a multiplier has made the number fractional. At the
           anchor size and biweekly, the tool must return the printed price. */
        if (mult !== 1) cleaning = round5(cleaning * mult);
      }
    }

    var junk = 0;
    if (s.service !== 'cleaning' && s.junkVolume) junk = RATES.junk[s.junkVolume] || 0;

    var credit = cleaning > 0 && junk > 0 ? RATES.bundleCredit || 0 : 0;
    var total = Math.max(0, cleaning + junk - credit);

    var isRecurring =
      s.service !== 'junk' &&
      (s.cleanType || 'recurring') === 'recurring' &&
      (s.frequency || 'biweekly') !== 'onetime';

    return {
      low: round5(total * 0.93),
      high: round5(total * 1.12),
      cleaning: cleaning,
      junk: junk,
      credit: credit,
      recurring: isRecurring,
      unit: isRecurring ? 'per visit' : 'one time'
    };
  }

  /* ------------------------------------------------------------- scoring */

  function qualify(s, est, inArea, hasPhone) {
    if (!inArea) {
      return {
        score: 10,
        tier: 'waitlist',
        annualValue: 0,
        signals: ['Outside current service area, added to expansion waitlist']
      };
    }

    var score = 25;
    var signals = [];

    if (s.service === 'both') {
      score += 20;
      signals.push('Wants cleaning and hauling, highest ticket combination');
    } else if (s.service === 'cleaning') score += 12;
    else score += 8;

    var freq = s.frequency || 'onetime';
    score += { weekly: 25, biweekly: 22, monthly: 16, onetime: 4 }[freq] || 0;
    if (freq === 'weekly' || freq === 'biweekly')
      signals.push('Recurring intent');

    var tl = s.timeline || 'planning';
    score += { asap: 15, 'two-weeks': 10, 'this-month': 6, planning: 0 }[tl] || 0;
    if (tl === 'asap') signals.push('Wants a crew this week');

    var beds = parseInt(s.bedrooms || '3', 10);
    if (beds >= 4) {
      score += 10;
      signals.push('Larger home, above average ticket');
    } else if (beds === 3) score += 7;
    else score += 4;

    if (hasPhone) {
      score += 8;
      signals.push('Gave a phone number');
    }

    var ct = s.customerType || 'homeowner';
    if (ct === 'realtor' || ct === 'property-manager' || ct === 'landlord') {
      score += 12;
      signals.push('Repeat commercial source, worth a direct call');
    } else if (ct === 'homeowner') score += 5;

    score = Math.min(100, score);

    return {
      score: score,
      tier: score >= 70 ? 'hot' : score >= 45 ? 'warm' : 'nurture',
      annualValue: Math.round(est.cleaning * (VISITS_PER_YEAR[freq] || 1) + est.junk),
      signals: signals
    };
  }

  /* --------------------------------------------------------- number roll */

  function rollTo(el, to) {
    var from = parseFloat(el.getAttribute('data-val') || '0');
    el.setAttribute('data-val', String(to));

    var reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || from === to) {
      el.textContent = money(to);
      return;
    }

    var t0 = null;
    function tick(now) {
      if (t0 === null) t0 = now;
      var p = Math.min(1, (now - t0) / 320);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = money(from + (to - from) * eased);
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* -------------------------------------------------------------- flow */

  function init(rootEl) {
    var STEPS = ['basics', 'details', 'when', 'contact'];
    /*
      The hero posts ?zip=01915 to this page. Liquid cannot read a query string,
      so the handoff is picked up here instead.
    */
    var seedZip = '';
    try {
      seedZip = (new URLSearchParams(window.location.search).get('zip') || '')
        .replace(/\D/g, '')
        .slice(0, 5);
    } catch (e) {
      seedZip = '';
    }

    var state = {
      zip: seedZip,
      // Junk removal is not offered right now, so the flow is cleaning-only.
      // Every downstream branch already keys off this value.
      service: 'cleaning',
      cleanType: 'recurring',
      frequency: 'biweekly',
      bedrooms: '3',
      bathrooms: '2',
      junkVolume: null,
      timeline: null,
      customerType: 'homeowner'
    };
    var step = 0;

    var q = function (sel) {
      return rootEl.querySelector(sel);
    };
    var qa = function (sel) {
      return Array.prototype.slice.call(rootEl.querySelectorAll(sel));
    };

    var zipInput = q('[data-zip-input]');
    if (state.zip && zipInput) zipInput.value = state.zip;

    function coverage() {
      if (state.zip.length !== 5) return null;
      var hit = GEO.zips[state.zip];
      return hit ? { covered: true, info: hit } : { covered: false };
    }

    function canAdvance() {
      if (STEPS[step] === 'basics') return state.zip.length === 5 && !!state.service;
      if (STEPS[step] === 'details')
        return state.service === 'cleaning' ? true : !!state.junkVolume;
      if (STEPS[step] === 'when') return !!state.timeline;
      return true;
    }

    function paint() {
      // Steps. Rendered eagerly and toggled, so no exit animation can strand the form.
      qa('[data-step]').forEach(function (el) {
        el.hidden = el.getAttribute('data-step') !== STEPS[step];
      });
      qa('[data-progress-bar]').forEach(function (el, i) {
        el.setAttribute('data-on', i <= step ? 'true' : 'false');
      });
      var counter = q('[data-progress-label]');
      if (counter) counter.textContent = step + 1 + ' of ' + STEPS.length;

      // Detail step only shows the halves that apply to the chosen service.
      var cleanBlock = q('[data-block="cleaning"]');
      var junkBlock = q('[data-block="junk"]');
      if (cleanBlock) cleanBlock.hidden = state.service === 'junk';
      if (junkBlock) junkBlock.hidden = state.service === 'cleaning';
      var freqBlock = q('[data-block="frequency"]');
      if (freqBlock) freqBlock.hidden = state.cleanType !== 'recurring';

      // Selected states.
      qa('[data-opt]').forEach(function (btn) {
        var group = btn.getAttribute('data-group');
        var on = String(state[group]) === btn.getAttribute('data-opt');
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
        btn.setAttribute('data-on', on ? 'true' : 'false');
      });

      var navBack = rootEl.querySelector('[data-next]')
        ? rootEl.querySelector('[data-next]').parentNode.querySelector('[data-back]')
        : null;
      if (navBack) navBack.hidden = step === 0 || STEPS[step] === 'contact';
      var next = q('[data-next]');
      if (next) {
        next.hidden = STEPS[step] === 'contact';
        next.disabled = !canAdvance();
      }
      var submit = q('[data-submit-wrap]');
      if (submit) submit.hidden = STEPS[step] !== 'contact';

      paintPanel();
    }

    function paintPanel() {
      var est = estimate(state);
      var cov = coverage();

      var covWrap = q('[data-coverage-wrap]');
      var covEl = q('[data-coverage]');
      if (covWrap && covEl) {
        if (!cov) covWrap.hidden = true;
        else {
          covWrap.hidden = false;
          covEl.innerHTML = cov.covered
            ? cov.info.town +
              ', ' +
              cov.info.state +
              ' is covered by our ' +
              cov.info.hub +
              ' crews.'
            : '<span class="opacity-70">' +
              state.zip +
              ' is outside our current area. Finish anyway and we will put you at the front of the queue when we open there.</span>';
        }
      }

      var has = !!state.service && est.high > 0;
      var filled = q('[data-est-filled]');
      var empty = q('[data-est-empty]');
      if (filled) filled.hidden = !has;
      if (empty) empty.hidden = has;
      if (!has) return;

      rollTo(q('[data-est-low]'), est.low);
      rollTo(q('[data-est-high]'), est.high);
      var unit = q('[data-est-unit]');
      if (unit) unit.textContent = est.unit;

      function line(name, val, show) {
        var row = q('[data-line="' + name + '"]');
        if (!row) return;
        row.hidden = !show;
        if (show) row.querySelector('[data-line-val]').textContent = val;
      }
      line('cleaning', money(est.cleaning), est.cleaning > 0);
      line('junk', money(est.junk), est.junk > 0);
      line('credit', '-' + money(est.credit), est.credit > 0);
    }

    /* ------------------------------------------------------- listeners */

    if (zipInput) {
      zipInput.addEventListener('input', function () {
        this.value = this.value.replace(/\D/g, '').slice(0, 5);
        state.zip = this.value;
        paint();
      });
    }

    qa('[data-opt]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var group = this.getAttribute('data-group');
        state[group] = this.getAttribute('data-opt');

        // Only a recurring plan has a frequency, and only recurring earns the offer.
        if (group === 'cleanType') {
          state.frequency = state.cleanType === 'recurring' ? 'biweekly' : 'onetime';
        }
        paint();
      });
    });

    var nextBtn = q('[data-next]');
    if (nextBtn)
      nextBtn.addEventListener('click', function () {
        if (!canAdvance()) return;
        if (step < STEPS.length - 1) step++;
        paint();
      });

    // `data-back` exists in the step nav and again inside the contact form, so
    // bind all of them rather than only the first match.
    qa('[data-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (step > 0) step--;
        paint();
      });
    });

    /* ---------------------------------------------------------- submit */

    var form = q('[data-quote-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var name = q('#ss-name').value.trim();
        var email = q('#ss-email').value.trim();
        var phone = q('#ss-phone').value.trim();
        var notes = q('#ss-notes').value.trim();

        var errs = {};
        if (name.length < 2) errs.name = 'Tell us who to ask for.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email))
          errs.email = 'We need a working email to send the quote to.';

        ['name', 'email'].forEach(function (f) {
          var el = q('[data-error="' + f + '"]');
          var input = q('#ss-' + f);
          if (el) {
            el.textContent = errs[f] || '';
            el.hidden = !errs[f];
          }
          if (input) {
            if (errs[f]) input.setAttribute('aria-invalid', 'true');
            else input.removeAttribute('aria-invalid');
          }
        });
        if (Object.keys(errs).length) return;

        var cov = coverage();
        var inArea = !!(cov && cov.covered);
        var est = estimate(state);
        var qual = qualify(state, est, inArea, !!phone);

        var payload = {
          kind: 'quote',
          source: 'shopify-theme',
          received_at: new Date().toISOString(),
          name: name,
          email: email,
          phone: phone,
          zip: state.zip,
          town: inArea ? cov.info.town : null,
          state: inArea ? cov.info.state : null,
          market: inArea ? cov.info.market : null,
          in_service_area: inArea,
          quote: {
            service: state.service,
            cleanType: state.service === 'junk' ? null : state.cleanType,
            frequency: state.service === 'junk' ? null : state.frequency,
            bedrooms: state.service === 'junk' ? null : state.bedrooms,
            bathrooms: state.service === 'junk' ? null : state.bathrooms,
            junkVolume: state.service === 'cleaning' ? null : state.junkVolume,
            timeline: state.timeline,
            customerType: state.customerType,
            notes: notes
          },
          estimate_low: est.low,
          estimate_high: est.high,
          recurring: est.recurring,
          score: qual.score,
          tier: qual.tier,
          annual_value: qual.annualValue,
          signals: qual.signals
        };

        var btn = q('[data-submit]');
        var errBox = q('[data-submit-error]');
        if (errBox) errBox.hidden = true;
        if (btn) {
          btn.disabled = true;
          btn.textContent = 'Sending';
        }

        send(payload)
          .then(function () {
            var done = q('[data-quote-done]');
            var live = q('[data-quote-live]');
            if (done && live) {
              done.querySelector('[data-done-name]').textContent = name.split(' ')[0];
              done.querySelector('[data-done-email]').textContent = email;
              live.hidden = true;
              done.hidden = false;
              done.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          })
          .catch(function () {
            if (errBox) errBox.hidden = false;
            if (btn) {
              btn.disabled = false;
              btn.textContent = 'Send me this quote';
            }
          });
      });
    }

    function send(payload) {
      // No endpoint configured: hand off to Shopify's own contact form so the
      // lead is still captured rather than silently dropped.
      if (!CFG.leadEndpoint) {
        return postToShopifyContact(payload);
      }
      /* The ops app expects x-lead-key. The apikey/Authorization pair here was
         written when this setting pointed at Supabase directly, and CORS on the
         ops endpoint only allows content-type and x-lead-key, so sending the
         old headers fails at preflight. */
      var headers = { 'content-type': 'application/json' };
      if (CFG.leadKey) headers['x-lead-key'] = CFG.leadKey;
      return fetch(CFG.leadEndpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      }).then(function (r) {
        if (!r.ok) throw new Error('lead endpoint ' + r.status);
      });
    }

    function postToShopifyContact(payload) {
      var body = new FormData();
      body.append('form_type', 'contact');
      body.append('utf8', '✓');
      body.append('contact[name]', payload.name);
      body.append('contact[email]', payload.email);
      body.append('contact[phone]', payload.phone);
      body.append(
        'contact[body]',
        'QUOTE REQUEST\n' +
          'Tier: ' + payload.tier + ' (score ' + payload.score + ')\n' +
          'ZIP: ' + payload.zip + (payload.town ? ' (' + payload.town + ', ' + payload.state + ')' : ' OUT OF AREA') + '\n' +
          'Estimate: $' + payload.estimate_low + ' to $' + payload.estimate_high + '\n' +
          'Year one value: $' + payload.annual_value + '\n\n' +
          'Details: ' + JSON.stringify(payload.quote, null, 2) + '\n\n' +
          'Why it scored this way:\n' + payload.signals.map(function (s) { return '  - ' + s; }).join('\n')
      );
      return fetch(CFG.contactFallback, { method: 'POST', body: body }).then(function (r) {
        if (!r.ok) throw new Error('contact form ' + r.status);
      });
    }

    paint();
  }

  function boot() {
    Array.prototype.slice
      .call(document.querySelectorAll('[data-quote-flow]'))
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
